// /sign-up — real registration form.
//
// Restyled to match /sign-in's hero+card layout (dark canvas, red glows,
// 2-column grid). Form logic untouched: useRegister() → identity-service
// /v1/auth/register → on success we bounce to /sign-in (verification
// email already sent by identity-service via notif-service).
//
// Fields:
//   - display_name  (required, 2+ chars)
//   - email         (required, RFC-ish syntax)
//   - password      (required, 8+ chars)
//   - Konfirmasyon  (must match password)
//   - TOS + Privacy + KVKK aydınlatma (combined, required)
//   - Açık Rıza pazarlama (separate, optional opt-in per KVKK)

import { useState, type FormEvent } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useRegister } from "@/lib/auth";
import { ApiError } from "@/lib/api";
import { Icons, NDMark } from "@/components/icons";

export const Route = createFileRoute("/sign-up")({
  component: SignUpPage,
});

function SignUpPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const nav = useNavigate();
  const register = useRegister();

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptedTos, setAcceptedTos] = useState(false);
  // Marketing consent — explicit opt-in, KVKK açık rıza.
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const passwordOk = password.length >= 8;
  const passwordsMatch = password === confirm;
  const formValid =
    displayName.trim().length >= 2 &&
    email.includes("@") &&
    passwordOk &&
    passwordsMatch &&
    acceptedTos;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!formValid) return;
    try {
      await register.mutateAsync({
        display_name: displayName.trim(),
        email: email.trim().toLowerCase(),
        password,
        locale: tr ? "tr" : "en",
        timezone: "Europe/Istanbul",
        // KVKK consent — identity-service v0.3.13+ writes
        // consent_marketing to identity.users + appends 4 audit rows
        // to identity.consent_log (tos / privacy / kvkk_aydinlatma /
        // marketing) with source='register', the caller's IP + UA.
        accept_tos: acceptedTos,
        consent_marketing: acceptedMarketing,
      });

      await nav({ to: "/sign-in" });
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        if (e.status === 409) {
          setErr(tr
            ? "Bu e-posta zaten kayıtlı. Giriş yap veya parolanı sıfırla."
            : "This email is already registered. Sign in or reset your password.");
        } else {
          setErr(e.message);
        }
      } else {
        setErr(tr
          ? "Kayıt sırasında bir hata oluştu. Lütfen tekrar dene."
          : "Something went wrong during sign-up. Please try again.");
      }
    }
  }

  return (
    <AuthCanvas>
      <HeroPane tr={tr} />

      {/* Right — form card */}
      <div className="card" style={{ padding: 32, background: "var(--surface)", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Kayıt" : "Sign up"}</div>
        <h2 style={{ fontSize: 24, marginBottom: 22 }}>{tr ? "Hesabını oluştur." : "Create your account."}</h2>

        {/* Google OAuth shortcut — same endpoint handles signup + signin
            (3-way upsert: by google_sub → by email → new user). KVKK
            consent for OAuth users is collected on first dashboard load. */}
        <a
          href={`${__API_BASE_URL__}/v1/auth/oauth/google/start?return_to=/app`}
          className="btn btn-secondary"
          style={{ width: "100%", justifyContent: "center", padding: "11px 16px", marginBottom: 18, textDecoration: "none" }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77a6.6 6.6 0 0 1-9.81-3.47H2.18v2.85A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M6.42 14.1A6.6 6.6 0 0 1 6.07 12c0-.72.12-1.43.35-2.1V7.05H2.18a11 11 0 0 0 0 9.9l4.24-2.85z" />
            <path fill="#EA4335" d="M12 5.4c1.62 0 3.07.56 4.21 1.65L19.36 4A11 11 0 0 0 12 1a11 11 0 0 0-9.82 6.05L6.42 9.9A6.6 6.6 0 0 1 12 5.4z" />
          </svg>
          {tr ? "Google ile kayıt ol" : "Sign up with Google"}
        </a>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, color: "var(--mu)", fontSize: 11, fontFamily: "var(--mono)" }}>
          <div style={{ flex: 1, height: 1, background: "var(--b1)" }} />
          {tr ? "VEYA E-POSTA İLE" : "OR WITH EMAIL"}
          <div style={{ flex: 1, height: 1, background: "var(--b1)" }} />
        </div>

        <form onSubmit={onSubmit}>
          <input
            className="input"
            type="text"
            autoComplete="name"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={tr ? "Ad Soyad / kullanıcı adı" : "Full name / username"}
            style={{ marginBottom: 10 }}
          />
          <input
            className="input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={tr ? "ornek@firma.com" : "you@company.com"}
            style={{ marginBottom: 10 }}
          />
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={tr ? "Parola (en az 8 karakter)" : "Password (min 8 characters)"}
            style={{ marginBottom: password.length > 0 && !passwordOk ? 4 : 10 }}
          />
          {password.length > 0 && !passwordOk ? (
            <div style={{ color: "var(--error)", fontSize: 11, marginBottom: 8 }}>
              {tr ? "En az 8 karakter." : "At least 8 characters."}
            </div>
          ) : null}
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={tr ? "Parola (tekrar)" : "Password (again)"}
            style={{ marginBottom: confirm.length > 0 && !passwordsMatch ? 4 : 16 }}
          />
          {confirm.length > 0 && !passwordsMatch ? (
            <div style={{ color: "var(--error)", fontSize: 11, marginBottom: 12 }}>
              {tr ? "Parolalar eşleşmiyor." : "Passwords don't match."}
            </div>
          ) : null}

          {/* TOS + Privacy + KVKK aydınlatma — bundled, required */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "var(--tx2)", marginBottom: 10, lineHeight: 1.5 }}>
            <input
              type="checkbox"
              required
              checked={acceptedTos}
              onChange={(e) => setAcceptedTos(e.target.checked)}
              style={{ marginTop: 3, accentColor: "var(--accent)" }}
            />
            <span>
              {tr ? (
                <>
                  <a href="https://nexusdijital.com/kullanim-sartlari" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>Kullanım Şartları</a>
                  {"'nı, "}
                  <a href="https://nexusdijital.com/gizlilik" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>Gizlilik Politikası</a>
                  {"'nı ve "}
                  <a href="https://nexusdijital.com/aydinlatma-metni" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>KVKK Aydınlatma Metni</a>
                  {"'ni okudum, kabul ediyorum."}
                </>
              ) : (
                <>
                  I have read and accept the{" "}
                  <a href="https://nexusdijital.com/kullanim-sartlari" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>Terms of Service</a>
                  ,{" "}
                  <a href="https://nexusdijital.com/gizlilik" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>Privacy Policy</a>
                  {" and "}
                  <a href="https://nexusdijital.com/aydinlatma-metni" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>KVKK Disclosure</a>
                  .
                </>
              )}
            </span>
          </label>

          {/* Açık rıza — pazarlama — explicit opt-in, optional */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "var(--tx2)", marginBottom: 18, lineHeight: 1.5 }}>
            <input
              type="checkbox"
              checked={acceptedMarketing}
              onChange={(e) => setAcceptedMarketing(e.target.checked)}
              style={{ marginTop: 3, accentColor: "var(--accent)" }}
            />
            <span>
              {tr ? (
                <>
                  Yeni özellik ve kampanyalardan haberdar olmak istiyorum{" "}
                  <span style={{ color: "var(--mu)" }}>(opsiyonel · KVKK açık rıza)</span>.
                </>
              ) : (
                <>
                  Send me product updates and announcements{" "}
                  <span style={{ color: "var(--mu)" }}>(optional)</span>.
                </>
              )}
            </span>
          </label>

          {err ? (
            <div role="alert" style={{ color: "var(--error)", fontSize: 12, marginBottom: 14 }}>
              {err}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={!formValid || register.isPending}
            className="btn btn-primary"
            style={{ width: "100%", justifyContent: "center", padding: "11px 16px", opacity: !formValid || register.isPending ? 0.6 : 1 }}
          >
            {register.isPending
              ? (tr ? "Kayıt oluşturuluyor…" : "Creating account…")
              : (tr ? "Hesabını oluştur" : "Create account")}
            <Icons.ArrowRight size={14} />
          </button>
        </form>

        <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--mu)" }}>
          <span>{tr ? "Zaten hesabın var mı?" : "Already have an account?"}</span>
          <Link to="/sign-in" className="btn btn-ghost btn-sm">
            {tr ? "Giriş yap" : "Sign in"}
          </Link>
        </div>
      </div>
    </AuthCanvas>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Shared layout chrome — keeps the four restyled auth pages visually in
// lockstep with /sign-in without yanking the existing AuthShell component
// (still used by other places).
// ──────────────────────────────────────────────────────────────────────────

export function AuthCanvas({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "var(--bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <div className="bg-grid" style={{ position: "absolute", inset: 0, opacity: 0.5 }} />
      <AuthLangToggle />
      <div
        style={{
          position: "absolute",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(224,45,28,0.2), transparent 60%)",
          right: -200,
          top: -200,
          filter: "blur(60px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(224,45,28,0.1), transparent 60%)",
          left: "20%",
          bottom: -100,
          filter: "blur(60px)",
        }}
      />

      <div
        className="auth-grid"
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 60,
          alignItems: "center",
          maxWidth: 1100,
          width: "90vw",
        }}
      >
        {children}
      </div>

      {/* Trademark footer — handoff verbatim */}
      <div
        className="mono"
        style={{
          position: "absolute",
          bottom: 20,
          left: 0,
          right: 0,
          textAlign: "center",
          fontSize: 10,
          color: "var(--mu)",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
        }}
      >
        Mesh Yazılım Teknoloji Ltd. Şti. ·{" "}
        <span style={{ color: "var(--red)" }}>Trademark Reg. 2024/161525</span> · İstanbul
      </div>
    </div>
  );
}

/**
 * AuthLangToggle — TR/EN segmented switch pinned top-right of the
 * auth canvas. Same shape as the dashboard's topbar toggle but
 * positioned absolutely so it sits above the hero+form columns.
 * Persists to localStorage 'nexus.locale' (same key as the topbar).
 */
export function AuthLangToggle() {
  const { i18n } = useTranslation("common");
  const lang = (i18n.resolvedLanguage ?? "tr").slice(0, 2);
  const setLang = (l: "tr" | "en") => {
    void i18n.changeLanguage(l);
    localStorage.setItem("nexus.locale", l);
    document.documentElement.lang = l;
  };
  return (
    <div
      style={{
        position: "absolute",
        top: 24,
        right: 24,
        zIndex: 2,
        display: "flex",
        height: 30,
        background: "var(--surface)",
        border: "1px solid var(--b2)",
        borderRadius: 8,
        padding: 2,
        alignItems: "center",
      }}
    >
      {(["tr", "en"] as const).map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => setLang(id)}
          title={id === "tr" ? "Türkçe" : "English"}
          style={{
            height: 24,
            padding: "0 10px",
            borderRadius: 5,
            fontSize: 11,
            fontFamily: "var(--mono)",
            fontWeight: 600,
            letterSpacing: "0.05em",
            background: lang === id ? "var(--elev)" : "transparent",
            color: lang === id ? "var(--tx)" : "var(--mu)",
            border: lang === id ? "1px solid var(--b3)" : "1px solid transparent",
            transition: "background 150ms, color 150ms",
            cursor: "pointer",
          }}
        >
          {id.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function HeroPane({ tr }: { tr: boolean }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
        <NDMark size={42} />
        <div>
          <div style={{ fontFamily: "var(--display)", fontWeight: 800, fontSize: 18, letterSpacing: "-0.02em" }}>
            <span style={{ color: "var(--red)" }}>N</span>EXUS{" "}
            <span style={{ color: "var(--red)" }}>D</span>İJİTAL
          </div>
          <div className="mono" style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            İstanbul · Private beta · 2026
          </div>
        </div>
      </div>

      <h1 style={{ fontFamily: "var(--display)", fontWeight: 800, fontSize: 56, letterSpacing: "-0.03em", lineHeight: 1.02, marginBottom: 20 }}>
        {tr ? (
          <>İçeriğin beyni.<br /><span style={{ color: "var(--red)" }}>Yayıncı değil.</span></>
        ) : (
          <>The content brain.<br /><span style={{ color: "var(--red)" }}>Not the publisher.</span></>
        )}
      </h1>
      <p style={{ fontSize: 16, color: "var(--tx2)", maxWidth: 480, lineHeight: 1.6, marginBottom: 32 }}>
        {tr
          ? "Tüm markaların için tek workspace. Her birinin sesini öğrenen AI. 5 platforma yerel gönderiler. Günlük üretim yapan ajanslar, freelancerlar ve operatörler için."
          : "One workspace for every brand you run. AI that learns each one's voice. Native posts across 5 platforms. Built for agencies, freelancers, and operators producing daily content at scale."}
      </p>

      {/* Marketing facts — product facts, not fake analytics. */}
      <div style={{ display: "flex", gap: 24, paddingTop: 24, borderTop: "1px solid var(--b1)" }}>
        {([
          ["5", tr ? "Platform" : "Platforms"],
          ["5dk", tr ? "Onboarding" : "Onboarding"],
          ["KVKK", tr ? "uyumlu" : "compliant"],
        ] as const).map(([v, l]) => (
          <div key={l}>
            <div className="t-num" style={{ fontSize: 22, color: "var(--tx)" }}>{v}</div>
            <div className="mono" style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 4 }}>
              {l}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
